import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, CheckCircle, Gift, Clock, Shield } from 'lucide-react';
import './MarketResearchPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MarketResearchPage = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isTerminated, setIsTerminated] = useState(false);
  const [terminateReason, setTerminateReason] = useState('');
  const [email, setEmail] = useState('');

  const sections = [
    {
      id: 'screening',
      title: 'Screening Questions',
      questions: [
        {
          id: 'q1',
          text: 'What is your current role?',
          type: 'single',
          required: true,
          options: ['Owner/Founder', 'CEO/President', 'HR Director/Manager', 'Operations Manager', 'Other (please specify)'],
          hasOther: true
        },
        {
          id: 'q2',
          text: 'How many employees does your company have?',
          type: 'single',
          required: true,
          options: ['1-4 employees', '5-9 employees', '10-24 employees', '25-49 employees', '50-99 employees', '100+ employees'],
          terminateOn: '100+ employees',
          terminateMessage: 'Thank you for your interest! This survey is designed for businesses with fewer than 100 employees.'
        },
        {
          id: 'q3',
          text: 'Where is your company located?',
          type: 'single',
          required: true,
          options: ['United States', 'Other country'],
          terminateOn: 'Other country',
          terminateMessage: 'Thank you for your interest! This survey is currently focused on U.S.-based businesses.'
        },
        {
          id: 'q4',
          text: 'Do you have decision-making authority or significant influence over HR, talent management, or hiring decisions?',
          type: 'single',
          required: true,
          options: ['Yes', 'No'],
          terminateOn: 'No',
          terminateMessage: 'Thank you for your interest! We\'re looking for respondents with HR decision-making authority.'
        },
        {
          id: 'q5',
          text: 'Which of the following best describes your current approach to HR and talent management?',
          type: 'multiple',
          required: true,
          options: [
            'We handle everything internally with manual processes',
            'We use HR software tools but manage everything ourselves',
            'We use a PEO (Professional Employer Organization)',
            'We work with external HR consultants or agencies',
            'We don\'t have a formal HR process yet'
          ]
        }
      ]
    },
    {
      id: 'current_state',
      title: 'Current State Assessment',
      questions: [
        {
          id: 'q6',
          text: 'How many hours per week do you and your team spend on HR and people management tasks?',
          type: 'single',
          required: true,
          options: ['0-5 hours', '6-10 hours', '11-15 hours', '16-20 hours', 'More than 20 hours', 'I\'m not sure']
        },
        {
          id: 'q7',
          text: 'Which HR and talent management tasks take up the most time?',
          type: 'multiple',
          required: true,
          maxSelect: 3,
          options: [
            'Recruiting and hiring',
            'Onboarding new employees',
            'Performance management and reviews',
            'Compliance and legal requirements',
            'Employee engagement and retention',
            'Compensation and benefits management',
            'Training and development',
            'Handling employee issues and conflicts'
          ]
        },
        {
          id: 'q8',
          text: 'What are your biggest challenges in managing people and HR?',
          type: 'multiple',
          required: true,
          maxSelect: 3,
          options: [
            'Lack of time to focus on people strategy',
            'Lack of HR expertise',
            'Compliance and legal confusion',
            'Difficulty finding the right talent',
            'Retaining good employees',
            'Budget constraints',
            'Tools and systems are fragmented or hard to use',
            'Building and maintaining company culture'
          ]
        },
        {
          id: 'q9',
          text: 'How confident are you in your current HR and talent management approach?',
          type: 'scale',
          required: true,
          scaleLabels: ['Not confident at all', 'Extremely confident']
        },
        {
          id: 'q10',
          text: 'What\'s the single most important outcome you\'d want from an HR solution?',
          type: 'single',
          required: true,
          options: [
            'Save time on administrative tasks',
            'Hire better people faster',
            'Improve employee retention',
            'Ensure compliance and reduce risk',
            'Build a stronger company culture',
            'Develop and grow my team'
          ]
        },
        {
          id: 'q11',
          text: 'How much do you currently spend per month on HR and talent management solutions?',
          type: 'single',
          required: true,
          subtitle: 'Include software, consultants, PEOs, etc.',
          options: ['$0 (we don\'t use any paid solutions)', '$1-$99', '$100-$249', '$250-$499', '$500-$999', '$1,000+']
        }
      ]
    },
    {
      id: 'channels',
      title: 'How You Stay Informed',
      description: 'These questions help us understand where small business owners discover and evaluate new HR, management, and business tools — so we can show up in the right places for you.',
      questions: [
        {
          id: 'q54',
          text: 'Which channels do you use to stay current on HR, management, and business tools?',
          type: 'multiple',
          options: [
            'LinkedIn', 'Facebook Groups', 'Instagram', 'YouTube', 'TikTok', 'Podcasts',
            'Email Newsletters', 'Industry Associations / Trade Groups', 'Peer Networks / Colleagues',
            'Google / Online Search', 'Business Publications (Inc., Forbes, HBR, etc.)',
            'Conferences & Events', 'Reddit / Online Forums', 'SMS / Text Communities', 'Other (please specify)'
          ],
          hasOther: true
        },
        {
          id: 'q55',
          text: 'Which ONE channel do you check most frequently for business and HR insights?',
          type: 'single',
          options: [
            'LinkedIn', 'Facebook Groups', 'Instagram', 'YouTube', 'TikTok', 'Podcasts',
            'Email Newsletters', 'Industry Associations / Trade Groups', 'Peer Networks / Colleagues',
            'Google / Online Search', 'Business Publications (Inc., Forbes, HBR, etc.)',
            'Conferences & Events', 'Reddit / Online Forums', 'SMS / Text Communities'
          ]
        },
        {
          id: 'q56',
          text: 'What type of content do you find most useful when evaluating a new business or HR tool?',
          type: 'multiple',
          options: [
            'Short-form video (Reels, TikTok, YouTube Shorts)',
            'Long-form video (YouTube, webinars)',
            'Written articles / Blog posts',
            'Podcasts / Audio content',
            'Case studies / Success stories',
            'Peer recommendations / Word of mouth',
            'Free trials / Product demos',
            'Infographics / Visual summaries',
            'Live events / Workshops',
            'Email newsletters',
            'Other (please specify)'
          ],
          hasOther: true
        },
        {
          id: 'q57',
          text: 'Which publications, podcasts, communities, or influencers do you trust most for business and HR advice?',
          type: 'text',
          placeholder: 'Be as specific as you like...'
        },
        {
          id: 'q58',
          text: 'How do you typically FIRST discover a new HR or business management tool?',
          type: 'single',
          options: [
            'A colleague or peer recommended it',
            'Saw an ad on social media',
            'Found it through a Google / online search',
            'Read about it in a newsletter or business publication',
            'Heard about it on a podcast',
            'Attended a webinar or live event',
            'My industry association or trade group recommended it',
            'A consultant or advisor suggested it',
            'Other (please specify)'
          ],
          hasOther: true
        }
      ]
    },
    {
      id: 'beliefs',
      title: 'Beliefs & Perceptions',
      questions: [
        {
          id: 'q12',
          text: 'Please rate your agreement with the following statements:',
          type: 'matrix',
          required: true,
          statements: [
            'My company\'s growth is limited by our ability to attract and retain talent',
            'I wish I had more time to focus on people strategy instead of HR admin',
            'Most HR solutions are too complex or expensive for businesses my size',
            'I\'d rather have tools + expert guidance than full-service outsourcing'
          ],
          scaleLabels: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
          scaleValues: 5
        },
        {
          id: 'q13',
          text: 'Which statement best describes your ideal HR solution?',
          type: 'single',
          required: true,
          options: [
            'Give me the tools and templates—I\'ll figure it out',
            'Give me tools AND guidance when I need it',
            'Handle most of it for me, I\'ll review and approve',
            'Take it all off my plate—just tell me what to sign'
          ]
        },
        {
          id: 'q14',
          text: 'How important is it that your HR solution is customized specifically for your industry?',
          type: 'scale',
          required: true,
          scaleLabels: ['Not important', 'Extremely important']
        },
        {
          id: 'q15',
          text: 'What concerns you most about adopting a new HR solution?',
          type: 'multiple',
          required: true,
          maxSelect: 2,
          options: [
            'The cost',
            'Time required to set up and learn',
            'It will be too complex for my team',
            'I\'ll lose control over my people processes',
            'It won\'t fit my specific needs',
            'The vendor won\'t provide good support'
          ]
        },
        {
          id: 'q16',
          text: 'In your opinion, what makes a good HR partner for small businesses?',
          type: 'text',
          placeholder: 'Share your thoughts...'
        }
      ]
    },
    {
      id: 'diy_platform',
      title: 'DIY Platform Testing',
      description: 'Imagine you had access to a platform with: Pre-built templates (job descriptions, interview scorecards, onboarding checklists), Step-by-step guides and best practices, Basic tools to track and manage your hiring and employee processes, No expert support—just tools and resources you can use on your own.',
      questions: [
        {
          id: 'q17',
          text: 'How likely would you be to use this kind of DIY platform?',
          type: 'scale',
          required: true,
          scaleLabels: ['Not likely at all', 'Extremely likely']
        },
        {
          id: 'q18',
          text: 'What would make you hesitate to use a DIY platform?',
          type: 'multiple',
          required: true,
          options: [
            'I don\'t have the HR expertise to do it myself',
            'I don\'t have time to learn and manage it',
            'I\'m worried I\'ll make costly mistakes',
            'I\'d still need someone to guide me through it',
            'Nothing—this sounds perfect for me'
          ]
        },
        {
          id: 'q19',
          text: 'What\'s the maximum you\'d be willing to pay per month for a DIY platform like this?',
          type: 'single',
          required: true,
          options: ['$0-$49', '$50-$99', '$100-$149', '$150-$199', '$200+', 'I wouldn\'t be interested in this']
        }
      ]
    },
    {
      id: 'concept',
      title: 'Introducing: The R6 Collective',
      isConceptIntro: true,
      conceptContent: {
        headline: 'A Talent Optimization Platform Built for Small Businesses',
        intro: 'The R6 Collective is an AI-powered platform that helps small businesses attract, develop, and retain the right talent—without the complexity or cost of traditional HR solutions.',
        differentiators: [
          { title: 'Flexible Support Levels', desc: 'Choose how much help you want—from DIY tools to full strategic guidance' },
          { title: 'Industry-Specific Templates', desc: 'Pre-built resources customized for your business type' },
          { title: 'AI-Powered Insights', desc: 'Get data-driven recommendations for hiring, engagement, and retention' },
          { title: 'Expert Access', desc: 'Connect with HR professionals when you need them (higher tiers only)' },
          { title: 'All-in-One Platform', desc: 'Manage recruiting, onboarding, performance, and development in one place' }
        ],
        mission: 'We believe small businesses deserve the same quality of talent strategy as Fortune 500 companies—but without the enterprise price tag or overwhelming complexity.',
        framework: 'The Right Six Framework: Our approach focuses on six core areas that drive business success: Right Roles, Right Talent, Right Onboarding, Right Development, Right Culture, and Right Strategy.',
        partnerSolutions: [
          {
            title: 'Employee Wellbeing & Peer Mentoring',
            features: ['Unlimited 1:1 sessions', '100+ life topics', 'Zero employee copay'],
            stat: '85%+ rebook rate'
          },
          {
            title: 'Workforce Training & Career Development',
            features: ['25+ cohort-based programs', 'Job-ready certifications', 'Management tracks'],
            stat: 'Build your talent pipeline fast'
          },
          {
            title: 'SMB Benefits, Section 125 & Payroll',
            features: ['Section 125 tax savings', 'Group Health', '$0-copay prescriptions'],
            stat: 'Potential $48K FICA savings'
          },
          {
            title: 'AI-Powered Sales Training & Coaching',
            features: ['Role-play simulations', 'Real-time coaching', 'Performance dashboards'],
            stat: '60-70% faster sales ramp time'
          }
        ]
      },
      questions: []
    },
    {
      id: 'reaction',
      title: 'Your Reaction to The R6 Collective',
      questions: [
        {
          id: 'q20',
          text: 'Based on what you just read, how interested are you in The R6 Collective?',
          type: 'scale',
          required: true,
          scaleLabels: ['Not interested at all', 'Extremely interested']
        },
        {
          id: 'q21',
          text: 'What do you find most appealing about The R6 Collective?',
          type: 'multiple',
          required: true,
          maxSelect: 2,
          options: [
            'Flexible support levels (DIY to full-service)',
            'Industry-specific templates and resources',
            'AI-powered insights and recommendations',
            'Access to HR experts when needed',
            'All-in-one platform (recruiting, onboarding, development)',
            'Specifically built for small businesses'
          ]
        },
        {
          id: 'q22',
          text: 'What concerns or questions do you have about The R6 Collective?',
          type: 'multiple',
          required: true,
          options: [
            'How much will it cost?',
            'How hard will it be to implement?',
            'Will the AI actually be helpful or just a gimmick?',
            'Will the HR experts really understand my business?',
            'How will it integrate with my existing systems?',
            'Will it really deliver better results than what I\'m doing now?',
            'No concerns—I\'m ready to learn more'
          ]
        },
        {
          id: 'q23',
          text: 'Which messaging statement best resonates with you?',
          type: 'single',
          required: true,
          options: [
            '"Stop wasting time on HR busywork. Build the team your business deserves."',
            '"Talent strategy shouldn\'t be just for big companies. Get expert-level HR without the enterprise price."',
            '"Your people are your biggest asset. Finally, tools and support to help them thrive."',
            '"Attract, develop, and retain top talent—all in one place, tailored for your business."'
          ]
        },
        {
          id: 'q24',
          text: 'If you were describing The R6 Collective to a friend, what would you say it is?',
          type: 'text',
          placeholder: 'Describe it in your own words...'
        },
        {
          id: 'q25',
          text: 'What would need to be true for you to seriously consider using The R6 Collective?',
          type: 'text',
          placeholder: 'Share what would convince you...'
        }
      ]
    },
    {
      id: 'pricing',
      title: 'Pricing Questions',
      description: 'The following questions help us understand the right price point for this type of solution.',
      questions: [
        {
          id: 'q26',
          text: 'At what price would you consider The R6 Collective to be so expensive that you would not consider buying it? (Too Expensive)',
          type: 'currency',
          required: true
        },
        {
          id: 'q27',
          text: 'At what price would you consider The R6 Collective to be expensive, but you would still consider buying it? (Expensive but Acceptable)',
          type: 'currency',
          required: true
        },
        {
          id: 'q28',
          text: 'At what price would you consider The R6 Collective to be a bargain—a great buy for the value? (Good Value)',
          type: 'currency',
          required: true
        },
        {
          id: 'q29',
          text: 'At what price would you consider The R6 Collective to be so inexpensive that you would question its quality? (Too Cheap)',
          type: 'currency',
          required: true
        }
      ]
    },
    {
      id: 'priorities',
      title: 'Understanding Your Priorities',
      questions: [
        {
          id: 'q30',
          text: 'If you had to choose, which would you prioritize?',
          type: 'single',
          required: true,
          options: [
            'Lower cost with DIY tools and templates',
            'Higher cost with expert guidance and support'
          ]
        },
        {
          id: 'q31',
          text: 'Would you pay more for industry-specific customization?',
          type: 'single',
          required: true,
          subtitle: 'e.g., templates and processes tailored to retail, healthcare, tech, etc.',
          options: [
            'Yes, I\'d pay significantly more (20%+ premium)',
            'Yes, I\'d pay somewhat more (10-20% premium)',
            'Yes, but only a little more (under 10% premium)',
            'No, I\'d expect it to be included at no extra cost'
          ]
        },
        {
          id: 'q32',
          text: 'What\'s the most important factor in your buying decision?',
          type: 'single',
          required: true,
          options: ['Price', 'Ease of use', 'Quality of support/guidance', 'Customization to my business', 'Proven results and ROI']
        },
        {
          id: 'q33',
          text: 'How do you typically prefer to pay for business software and services?',
          type: 'single',
          required: true,
          options: [
            'Monthly subscription',
            'Annual subscription (with discount)',
            'Per employee pricing',
            'One-time purchase',
            'I\'m flexible—whatever offers the best value'
          ]
        }
      ]
    },
    {
      id: 'features',
      title: 'Feature Prioritization',
      questions: [
        {
          id: 'q34',
          text: 'Please rate how important each feature would be to you:',
          type: 'matrix',
          required: true,
          statements: [
            'Pre-built job description templates',
            'Applicant tracking and interview management',
            'Onboarding checklists and workflows',
            'Performance review templates and tracking',
            'Employee development and training plans',
            'AI-powered recommendations and insights'
          ],
          scaleLabels: ['Not important', 'Somewhat important', 'Important', 'Very important', 'Critical'],
          scaleValues: 5
        },
        {
          id: 'q35',
          text: 'Which ONE feature would you consider a "must-have" before you\'d even consider purchasing?',
          type: 'single',
          required: true,
          options: [
            'Pre-built job description templates',
            'Applicant tracking and interview management',
            'Onboarding checklists and workflows',
            'Performance review templates and tracking',
            'Employee development and training plans',
            'AI-powered recommendations and insights',
            'Access to HR experts'
          ]
        },
        {
          id: 'q36',
          text: 'Are there any features or capabilities we haven\'t mentioned that would be important to you?',
          type: 'text',
          placeholder: 'Share any missing features...'
        }
      ]
    },
    {
      id: 'platform',
      title: 'Platform Requirements',
      questions: [
        {
          id: 'q37',
          text: 'How important is it that the platform integrates with tools you already use?',
          type: 'scale',
          required: true,
          scaleLabels: ['Not important', 'Critical']
        },
        {
          id: 'q38',
          text: 'Which integrations would be most valuable to you?',
          type: 'multiple',
          required: true,
          options: [
            'Payroll systems (Gusto, ADP, etc.)',
            'Calendar tools (Google Calendar, Outlook)',
            'Job boards (Indeed, LinkedIn)',
            'Communication tools (Slack, Teams)',
            'E-signature tools (DocuSign, HelloSign)',
            'CRM systems',
            'None—I\'d prefer an all-in-one solution'
          ]
        },
        {
          id: 'q39',
          text: 'How important is mobile accessibility?',
          type: 'scale',
          required: true,
          subtitle: 'Being able to manage HR tasks from your phone',
          scaleLabels: ['Not important', 'Critical']
        },
        {
          id: 'q40',
          text: 'How important are data and analytics features?',
          type: 'scale',
          required: true,
          subtitle: 'Dashboards, reports, hiring metrics, retention data, etc.',
          scaleLabels: ['Not important', 'Critical']
        },
        {
          id: 'q41',
          text: 'How comfortable are you with AI-powered tools?',
          type: 'single',
          required: true,
          options: [
            'Very comfortable—I use AI tools regularly',
            'Somewhat comfortable—I\'ve tried a few',
            'Neutral—I\'m open to learning',
            'Skeptical—I\'d need proof it works',
            'Uncomfortable—I prefer human-only solutions'
          ]
        },
        {
          id: 'q42',
          text: 'If The R6 Collective launched in phases, which features would you want FIRST?',
          type: 'multiple',
          required: true,
          maxSelect: 3,
          options: [
            'Recruiting and hiring tools',
            'Onboarding workflows',
            'Performance management',
            'Employee development and training',
            'Engagement and retention tools',
            'Compliance and documentation'
          ]
        },
        {
          id: 'q43',
          text: 'Would you be interested in a founding member pilot program? Early access with 50% off for the first 6 months.',
          type: 'single',
          required: true,
          options: [
            'Yes, tell me more',
            'Maybe, depends on details',
            'No, I\'d wait for the full launch'
          ]
        }
      ]
    },
    {
      id: 'partner_solutions',
      title: 'Partner Solutions',
      description: 'The R6 Collective brings together four specialized partner solutions to cover the full talent lifecycle — wellbeing, training, benefits, and sales performance.',
      questions: [
        {
          id: 'q49',
          text: 'Which of these integrated partner solutions would add the most value for your business?',
          type: 'multiple',
          required: true,
          maxSelect: 2,
          options: [
            'Employee Wellbeing & Peer Mentoring — Unlimited 1:1 sessions, 100+ life topics, zero employee copay',
            'Workforce Training & Career Development — 25+ cohort-based programs, job-ready certifications & management tracks',
            'SMB Benefits & Payroll Solutions — Section 125 tax savings, Group Health, integrated payroll (potential $48K FICA savings)',
            'AI-Powered Sales Training & Coaching — Role-play simulations, real-time coaching, 60–70% faster sales ramp time',
            'None of these are relevant to my business right now'
          ]
        },
        {
          id: 'q50',
          text: 'How important is employee wellbeing and mental health support (e.g., peer mentoring, career guidance) to your talent retention strategy?',
          type: 'scale',
          required: true,
          scaleLabels: ['Not important', 'Critical']
        },
        {
          id: 'q51',
          text: 'How valuable would access to structured workforce training and career pathway programs be for your team?',
          type: 'scale',
          required: true,
          subtitle: 'e.g., leadership development, skills-based cohorts, job-ready certifications',
          scaleLabels: ['Not valuable', 'Extremely valuable']
        },
        {
          id: 'q52',
          text: 'How interested are you in accessing Fortune 1000-level employee benefits (Group Health, life insurance, Section 125 tax savings) through a simplified SMB platform?',
          type: 'scale',
          required: true,
          scaleLabels: ['Not interested', 'Very interested']
        },
        {
          id: 'q53',
          text: 'Which of the following best describes your team\'s current sales training situation?',
          type: 'single',
          required: true,
          options: [
            'We have a structured, effective sales training program in place',
            'We do some training but it\'s informal or inconsistent',
            'We lack adequate sales training and it\'s impacting our revenue',
            'We don\'t have a sales function (not applicable)'
          ]
        }
      ]
    },
    {
      id: 'tiers',
      title: 'Pricing Tiers & Purchase Intent',
      description: 'Here are four potential pricing tiers for The R6 Collective. Please review each and answer the following questions.',
      tierInfo: [
        {
          name: 'DIY Tier',
          price: '$97/month',
          features: ['Pre-built templates and tools', 'Self-service platform access', 'Knowledge base and guides', 'Email support', 'Partner solutions available as à la carte add-ons']
        },
        {
          name: 'Guided Tier',
          price: '$199/month',
          features: ['Everything in DIY, plus:', 'AI-powered recommendations', 'Monthly check-ins with HR experts', 'Priority support', 'Employee Wellbeing & Peer Mentoring Program']
        },
        {
          name: 'Professional Tier',
          price: '$349/month',
          features: ['Everything in Guided, plus:', 'Dedicated HR strategist', 'Customized playbooks', 'Bi-weekly strategy sessions', 'Advanced analytics', 'Workforce Training & Career Pathways', 'SMB Benefits, Section 125 & Payroll']
        },
        {
          name: 'Premium Tier',
          price: '$599/month',
          features: ['Everything in Professional, plus:', 'Weekly strategic support', 'On-demand expert access', 'Executive coaching', 'Custom integrations', 'Full Partner Suite Included']
        }
      ],
      questions: [
        {
          id: 'q45',
          text: 'Which tier best fits your needs?',
          type: 'single',
          required: true,
          options: [
            'DIY Tier ($97/month)',
            'Guided Tier ($199/month)',
            'Professional Tier ($349/month)',
            'Premium Tier ($599/month)',
            'None of these work for me'
          ]
        },
        {
          id: 'q46',
          text: 'How likely are you to purchase your selected tier in the next 3-6 months?',
          type: 'scale',
          required: true,
          scaleLabels: ['Not likely at all', 'Extremely likely']
        },
        {
          id: 'q47',
          text: 'What would make you MORE likely to purchase?',
          type: 'multiple',
          required: true,
          options: [
            'Lower price point',
            'Free trial period',
            'Money-back guarantee',
            'Case studies or testimonials from similar businesses',
            'Live demo or walkthrough',
            'Clear ROI calculator or data',
            'Nothing—I\'m ready now'
          ]
        },
        {
          id: 'q48',
          text: 'Any final thoughts, questions, or feedback about The R6 Collective?',
          type: 'text',
          placeholder: 'Share your final thoughts...'
        }
      ]
    }
  ];

  const handleSingleSelect = (questionId, value, question) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    
    // Check for termination conditions
    if (question.terminateOn && value === question.terminateOn) {
      setTerminateReason(question.terminateMessage);
      setIsTerminated(true);
    }
  };

  const handleMultiSelect = (questionId, value, maxSelect) => {
    setAnswers(prev => {
      const current = prev[questionId] || [];
      if (current.includes(value)) {
        return { ...prev, [questionId]: current.filter(v => v !== value) };
      } else {
        if (maxSelect && current.length >= maxSelect) {
          return prev;
        }
        return { ...prev, [questionId]: [...current, value] };
      }
    });
  };

  const handleScaleSelect = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleMatrixSelect = (questionId, statementIndex, value) => {
    setAnswers(prev => {
      const current = prev[questionId] || {};
      return { ...prev, [questionId]: { ...current, [statementIndex]: value } };
    });
  };

  const handleTextInput = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleOtherInput = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [`${questionId}_other`]: value }));
  };

  const handleNext = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/market-research`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers, email, timestamp: new Date().toISOString() })
      });
      if (response.ok) {
        setIsComplete(true);
      }
    } catch (error) {
      console.error('Error submitting survey:', error);
      setIsComplete(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestion = (question, idx) => {
    switch (question.type) {
      case 'single':
        return (
          <div className="mr-options" key={question.id}>
            {question.options.map((option, optIdx) => (
              <label key={optIdx} className={`mr-option ${answers[question.id] === option ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name={question.id}
                  checked={answers[question.id] === option}
                  onChange={() => handleSingleSelect(question.id, option, question)}
                />
                <span className="mr-option-radio"></span>
                <span className="mr-option-text">{option}</span>
              </label>
            ))}
            {question.hasOther && answers[question.id]?.includes('Other') && (
              <input
                type="text"
                className="mr-other-input"
                placeholder="Please specify..."
                value={answers[`${question.id}_other`] || ''}
                onChange={(e) => handleOtherInput(question.id, e.target.value)}
              />
            )}
          </div>
        );

      case 'multiple':
        return (
          <div className="mr-options mr-options-multi" key={question.id}>
            {question.maxSelect && (
              <p className="mr-select-limit">Select up to {question.maxSelect}</p>
            )}
            {question.options.map((option, optIdx) => (
              <label key={optIdx} className={`mr-option ${(answers[question.id] || []).includes(option) ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={(answers[question.id] || []).includes(option)}
                  onChange={() => handleMultiSelect(question.id, option, question.maxSelect)}
                />
                <span className="mr-option-checkbox"></span>
                <span className="mr-option-text">{option}</span>
              </label>
            ))}
            {question.hasOther && (answers[question.id] || []).some(a => a.includes('Other')) && (
              <input
                type="text"
                className="mr-other-input"
                placeholder="Please specify..."
                value={answers[`${question.id}_other`] || ''}
                onChange={(e) => handleOtherInput(question.id, e.target.value)}
              />
            )}
          </div>
        );

      case 'scale':
        return (
          <div className="mr-scale" key={question.id}>
            <div className="mr-scale-labels">
              <span>{question.scaleLabels[0]}</span>
              <span>{question.scaleLabels[1]}</span>
            </div>
            <div className="mr-scale-options">
              {[1, 2, 3, 4, 5].map(num => (
                <button
                  key={num}
                  type="button"
                  className={`mr-scale-btn ${answers[question.id] === num ? 'selected' : ''}`}
                  onClick={() => handleScaleSelect(question.id, num)}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
        );

      case 'matrix':
        return (
          <div className="mr-matrix" key={question.id}>
            <div className="mr-matrix-header">
              <div className="mr-matrix-scale-labels">
                {question.scaleLabels.map((label, i) => (
                  <span key={i}>{label}</span>
                ))}
              </div>
            </div>
            {question.statements.map((statement, stmtIdx) => (
              <div key={stmtIdx} className="mr-matrix-row">
                <div className="mr-matrix-statement">{statement}</div>
                <div className="mr-matrix-options">
                  {Array.from({ length: question.scaleValues || 5 }, (_, i) => i + 1).map(num => (
                    <button
                      key={num}
                      type="button"
                      className={`mr-matrix-btn ${(answers[question.id] || {})[stmtIdx] === num ? 'selected' : ''}`}
                      onClick={() => handleMatrixSelect(question.id, stmtIdx, num)}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );

      case 'text':
        return (
          <textarea
            key={question.id}
            className="mr-textarea"
            placeholder={question.placeholder || "Enter your response..."}
            value={answers[question.id] || ''}
            onChange={(e) => handleTextInput(question.id, e.target.value)}
            rows={4}
          />
        );

      case 'currency':
        return (
          <div className="mr-currency" key={question.id}>
            <span className="mr-currency-symbol">$</span>
            <input
              type="number"
              className="mr-currency-input"
              placeholder="0"
              value={answers[question.id] || ''}
              onChange={(e) => handleTextInput(question.id, e.target.value)}
            />
            <span className="mr-currency-label">per month</span>
          </div>
        );

      default:
        return null;
    }
  };

  const currentSectionData = sections[currentSection];
  const progress = ((currentSection + 1) / sections.length) * 100;

  // Terminated screen
  if (isTerminated) {
    return (
      <div className="mr-page">
        <div className="mr-complete">
          <div className="mr-complete-icon mr-terminate-icon">
            <Shield size={80} />
          </div>
          <h1>Thank You!</h1>
          <p>{terminateReason}</p>
          <p className="mr-complete-note">We appreciate your time and interest in The R6 Collective.</p>
        </div>
      </div>
    );
  }

  // Complete screen
  if (isComplete) {
    return (
      <div className="mr-page">
        <div className="mr-complete">
          <div className="mr-complete-icon">
            <CheckCircle size={80} />
          </div>
          <h1>Thank You!</h1>
          <p>Your responses have been successfully submitted. We truly appreciate you taking the time to share your insights with us.</p>
          <div className="mr-complete-gift">
            <Gift size={32} />
            <span>You're entered to win one of five $200 Amazon gift cards!</span>
          </div>
          <p className="mr-complete-note">Winners will be notified by email within 2 weeks.</p>
          <p className="mr-complete-cta">Want to stay updated on The R6 Collective launch? Visit <a href="https://r6collective.com" target="_blank" rel="noopener noreferrer">r6collective.com</a></p>
        </div>
      </div>
    );
  }

  return (
    <div className="mr-page">
      <div className="mr-header">
        <h1 className="mr-title">THE R6 COLLECTIVE™</h1>
        <p className="mr-subtitle">Market Research Survey</p>
        <div className="mr-incentive">
          <Gift size={20} />
          <span>Complete the survey to enter to win one of five $200 Amazon gift cards</span>
        </div>
        <div className="mr-meta">
          <span><Clock size={16} /> ~15–18 minutes</span>
          <span><Shield size={16} /> All responses anonymous</span>
        </div>
      </div>

      <div className="mr-progress-container">
        <div className="mr-progress-bar">
          <div className="mr-progress-fill" style={{ width: `${progress}%` }}></div>
        </div>
        <div className="mr-progress-text">
          Section {currentSection + 1} of {sections.length}: {currentSectionData.title}
        </div>
      </div>

      <div className="mr-content">
        <div className="mr-section">
          <h2 className="mr-section-title">{currentSectionData.title}</h2>
          {currentSectionData.description && (
            <p className="mr-section-desc">{currentSectionData.description}</p>
          )}

          {/* Concept Introduction */}
          {currentSectionData.isConceptIntro && currentSectionData.conceptContent && (
            <div className="mr-concept">
              <h3 className="mr-concept-headline">{currentSectionData.conceptContent.headline}</h3>
              <p className="mr-concept-intro">{currentSectionData.conceptContent.intro}</p>
              
              <h4 className="mr-concept-subhead">What makes us different:</h4>
              <div className="mr-differentiators">
                {currentSectionData.conceptContent.differentiators.map((item, idx) => (
                  <div key={idx} className="mr-diff-item">
                    <strong>{item.title}:</strong> {item.desc}
                  </div>
                ))}
              </div>

              <div className="mr-mission-box">
                <h4>Our Mission</h4>
                <p>{currentSectionData.conceptContent.mission}</p>
              </div>

              <p className="mr-framework">{currentSectionData.conceptContent.framework}</p>

              <h4 className="mr-concept-subhead">Integrated Partner Solutions — Fortune 1000 Capabilities for SMBs:</h4>
              <div className="mr-solutions-grid">
                {currentSectionData.conceptContent.partnerSolutions.map((solution, idx) => (
                  <div key={idx} className="mr-solution-card">
                    <h5>{solution.title}</h5>
                    <ul>
                      {solution.features.map((feature, fIdx) => (
                        <li key={fIdx}>{feature}</li>
                      ))}
                    </ul>
                    <div className="mr-solution-stat">{solution.stat}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tier Cards */}
          {currentSectionData.tierInfo && (
            <div className="mr-tiers">
              {currentSectionData.tierInfo.map((tier, idx) => (
                <div key={idx} className="mr-tier-card">
                  <div className="mr-tier-header">
                    <h4>{tier.name}</h4>
                    <span className="mr-tier-price">{tier.price}</span>
                  </div>
                  <ul>
                    {tier.features.map((feature, fIdx) => (
                      <li key={fIdx}>{feature}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* Questions */}
          {currentSectionData.questions.map((question, idx) => (
            <div key={question.id} className="mr-question">
              <p className="mr-question-text">
                <span className="mr-question-num">{idx + 1}.</span> {question.text}
                {question.required && <span className="mr-required">*</span>}
              </p>
              {question.subtitle && (
                <p className="mr-question-subtitle">{question.subtitle}</p>
              )}
              {renderQuestion(question, idx)}
            </div>
          ))}

          {/* Email Capture on last section */}
          {currentSection === sections.length - 1 && (
            <div className="mr-email-capture">
              <h3>Enter Your Email for Gift Card Drawing</h3>
              <p>Enter your email to be included in the $200 Amazon gift card drawing and receive updates about The R6 Collective.</p>
              <input
                type="email"
                className="mr-email-input"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          )}
        </div>
      </div>

      <div className="mr-navigation">
        <button
          type="button"
          className="mr-nav-btn mr-nav-prev"
          onClick={handlePrevious}
          disabled={currentSection === 0}
        >
          <ChevronLeft size={20} />
          Previous
        </button>
        
        {currentSection === sections.length - 1 ? (
          <button
            type="button"
            className="mr-nav-btn mr-nav-submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Survey'}
          </button>
        ) : (
          <button
            type="button"
            className="mr-nav-btn mr-nav-next"
            onClick={handleNext}
          >
            Next
            <ChevronRight size={20} />
          </button>
        )}
      </div>

      <div className="mr-footer">
        <p>© 2026 The R6 Collective™ • Confidential Market Research</p>
        <p>Questions? Email us at <a href="mailto:inquire@r6collective.com">inquire@r6collective.com</a></p>
      </div>
    </div>
  );
};

export default MarketResearchPage;
